<template>
  <div :class="{'dialog-card v-card v-sheet theme--light' : isDialog }">
    <v-toolbar flat light color="white" v-if="$vuetify.breakpoint.smAndDown && isDialog">
      <v-toolbar-title v-if="hasActivePaymentPlan">{{ $t('ddtd-text-8') }}</v-toolbar-title>
      <v-toolbar-title v-else>{{ $t('ddtd-text-7') }}</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn icon @click="$emit('close')">
        <v-icon>fa-times</v-icon>
      </v-btn>
    </v-toolbar>
    <v-divider></v-divider>
    <v-container class="grow" fluid>
      <v-row no-gutters>
        <v-col cols="12" class="text-right" v-if="$vuetify.breakpoint.mdAndUp && isDialog">
          <v-btn color="color-grey" depressed right text small @click="$emit('close')">
            <v-icon class="mr-3">fa-times</v-icon>
            <span>{{ $t('close') }}</span>
          </v-btn>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" class="text-center">
          <template v-if="$vuetify.breakpoint.mdAndUp">
            <h4 class="mt-0 text-h4" v-if="hasActivePaymentPlan">{{ $t('ddtd-text-8') }}</h4>
            <h4 class="mt-0 text-h4" v-else>{{ $t('ddtd-text-7') }}</h4>
          </template>
          <p v-if="isMoveAllowed">{{ $t('ddtd-text-1') }}</p>
        </v-col>
        <v-col cols="12" v-if="!isMoveAllowed">
          <v-row justify="center">
            <v-col cols="12" sm="9">
              <v-card outlined>
                <v-container class="pa-6">
                  <v-row align="center">
                    <v-col class="shrink">
                      <v-icon>fas fa-info-circle</v-icon>
                    </v-col>
                    <v-col>
                      <span>{{ this.$t('paymentPlanResults.' + debt.duedateTransferParameters.result) }}</span>
                    </v-col>
                  </v-row>
                </v-container>
              </v-card>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <v-row justify="center" v-if="isMoveAllowed">
        <v-col cols="12" sm="9">
          <v-date-picker v-model="dueDate"
                         :locale="pickerLocale"
                         full-width
                         :landscape="$vuetify.breakpoint.mdAndUp"
                         first-day-of-week="1"
                         :min="minDate"
                         :max="maxDate"
                         :readonly="!isMoveAllowed"
                         :allowed-dates="allowedDates"
                         :event-color="date => date === originalDueDate ? 'primary' : 'error'"
                         :events="eventDates">
          </v-date-picker>
        </v-col>
      </v-row>
      <v-row v-if="isMoveAllowed && addCostToTransfer">
        <v-col cols="12">
          <div class="warning--text text-center">{{ $t('ddtd-text-2') }} {{ debt.duedateTransferParameters.cost | currency }}.</div>
          <div class="warning--text text-center" v-if="hasActivePaymentPlan">{{ $t('ddtd-text-5') }}</div>
        </v-col>
      </v-row>
      <v-row justify="center" v-if="isMoveAllowed">
        <div class="pa-3">
          <p class="ok-title">{{ $t('ddtd-text-3') }}</p>
          <b class="text-h6">{{ originalDueDate | formatDate}}</b>
        </div>
        <div class="pa-3">
          <p class="ok-title">{{ $t('ddtd-text-4') }}</p>
          <b class="text-h6">{{ dueDate | formatDate}}</b>
        </div>
      </v-row>
      <v-row v-if="isMoveAllowed">
        <v-col>
          <p class="text-center">{{ $t('ddtd-text-6') }}</p>
        </v-col>
      </v-row>
    </v-container>
    <v-card-actions class="justify-center">
      <div class="pa-3">
        <v-btn tile color="primary" @click="moveDueDate" :disabled="loading" :loading="loading" v-if="isMoveAllowed">
          {{ $t('transferDueDate') }}
        </v-btn>
      </div>
      <div class="pa-3">
        <v-btn tile outlined color="color-new-link-blue" @click="$emit('close')" v-if="isDialog">{{ $t('close') }}</v-btn>
      </div>
    </v-card-actions>
  </div>
</template>

<script>
  import moment from 'moment'

  export default {
    props: ['debt', 'dialog'],
    mounted () {
      let date = this.debt.dueDate != null
        ? moment(this.debt.dueDate)
        : null

      if (date != null) {
        this.originalDueDate = date.format('YYYY-MM-DD')
        if (date.isBefore(moment())) {
          this.dueDate = null
        } else {
          this.dueDate = date.format('YYYY-MM-DD')
        }
      } else {
        this.originalDueDate = null
        this.dueDate = null
      }
    },
    data: function () {
      return {
        dueDate: null,
        originalDueDate: null,
        loading: false
      }
    },
    methods: {
      allowedDates (date) {
        date = moment(date)
        let disallow = false
        disallow = this.disallowedDates.some(function (k) {
          return k.isSame(date, 'day')
        })
        return date.isoWeekday() < 6 && !disallow
      },
      moveDueDate () {
        let debtor = this.$store.getters.debtor
        let payload = { DebtorNr: debtor.nr, DebtNr: this.debt.orderNr, NewDuedate: this.dueDate }
        this.loading = true

        this.$store.dispatch('transferDueDate', payload).then((response) => {
          this.loading = false
          if (response != null && response === 0) {
            this.$store.commit('setDueDate', payload)
            this.$store.commit('setMessage', { type: 'primary', text: this.$t('dueDateTransferSuccess'), clickToDismiss: true })
            this.$emit('close')
            this.$store.dispatch('silentInitialize', { type: 'duedate' })
            this.$matomo && this.$matomo.trackEvent("Management", "Move Due Date", "Move Date Completed")
          } else {
            this.$store.commit('setMessage', { type: 'error', text: 'Eräpäivän siirrossa tapahtui virhe, Syy: ' + this.$t('dueDateDenied.' + response), clickToDismiss: true })
          }
        }).catch(() => {
          this.loading = false
          this.dueDate = this.debt.dueDate != null
            ? moment(this.debt.dueDate).toDate()
            : null
          this.$store.commit('setMessage', { type: 'error', text: 'Eräpäivän siirrossa tapahtui virhe', clickToDismiss: true })
        })
      }
    },
    computed: {
      hasActivePaymentPlan () {
        return this.debt.paymentPlan !== null
      },
      disallowedDates () {
        let dates = []
        if (this.debt.duedateTransferParameters.notAllowedDuedates.length) {
          this.debt.duedateTransferParameters.notAllowedDuedates.forEach((date) => {
            dates.push(moment(date))
          })
        }
        return dates
      },
      eventDates () {
        let dates = []
        if (this.originalDueDate !== null) {
          dates.push(this.originalDueDate)
        }
        this.debt.duedateTransferParameters.notAllowedDuedates.forEach((date) => {
          dates.push(moment(date).format('YYYY-MM-DD'))
        })
        return dates
      },
      isMoveAllowed () {
        this.$matomo && this.$matomo.trackEvent("Management", "Move Due Date", "Move Date Start")
        var allowed = this.debt.duedateTransferParameters.allow
        if (allowed) {
          this.$matomo && this.$matomo.trackEvent("Management", "Move Due Date", "Move Date Allowed")
        } else {
          this.$matomo && this.$matomo.trackEvent("Management", "Move Due Date", "Move Date Not Allowed")
        }
        return allowed
      },
      minDate () {
        if (moment(this.originalDueDate) > moment()) {
          return moment(this.originalDueDate).format('YYYY-MM-DD')
        } else {
          return moment().add(1, 'days').format('YYYY-MM-DD')
        }
      },
      maxDate () {
        return this.debt.duedateTransferParameters.maxNextDuedate !== null
          ? moment(this.debt.duedateTransferParameters.maxNextDuedate).format('YYYY-MM-DD')
          : null
      },
      pickerLocale () {
        return this.$store.getters.longLocale
      },
      addCostToTransfer () {
        if (this.debt.duedateTransferParameters.costlessDuedateTransfersLeft > 0 ||
            this.dueDate === null ||
            this.debt.duedateTransferParameters.cost === 0) {
          return false
        }

        let maxTransferWithoutCost = 14
        let compareDate = moment()

        if (this.originalDueDate !== null && moment(this.originalDueDate) > compareDate) {
          compareDate = moment(this.originalDueDate).add(maxTransferWithoutCost, 'days')
        } else {
          compareDate = compareDate.add(maxTransferWithoutCost, 'days')
        }
        compareDate = compareDate.startOf('day')

        if (moment(this.dueDate).isSameOrAfter(compareDate)) {
          return true
        } else {
          return false
        }
      },
      isDialog () {
        if (this.dialog !== undefined && this.dialog !== null) {
          return this.dialog
        } else {
          return true
        }
      }
    }
  }
</script>

<i18n>
  {
  "fi": {
  "ddtd-text-1": "Voit muuttaa maksun eräpäivää sinulle paremmin sopivaksi:",
  "ddtd-text-2": "Eräpäivän siirtäminen maksaa",
  "ddtd-text-3": "Vanha eräpäivä",
  "ddtd-text-4": "Uusi eräpäivä",
  "ddtd-text-5": "Kulu lisätään maksusuunnitelman viimeiseen erään.",
  "ddtd-text-6": "Maksettava summaa saattaa muuttua hieman, kun päivitämme koron uudelle eräpäivälle.",
  "ddtd-text-7": "Siirrä velan maksupäivä",
  "ddtd-text-8": "Siirrä maksun maksupäivä"
  },
  "se": {
  "ddtd-text-1": "Du kan flytta förfallodagen så att den passar dig bättre:",
  "ddtd-text-2": "Förflyttning av förfallodag kostar:",
  "ddtd-text-3": "Tidigare förfallodag",
  "ddtd-text-4": "Ny förfallodag",
  "ddtd-text-5": "Kostnaden läggs till i betalningsplanens sista rat.",
  "ddtd-text-6": "Summan som skall betalas kan eventuellt ändra då räntan uppdateras till en ny förfallodag.",
  "ddtd-text-7": "Flytta skuldens förfallodag",
  "ddtd-text-8": "Flytta delbetalningens förfallodag"
  },
  "en": {
  "ddtd-text-1": "You can change the due date to a more suitable date:",
  "ddtd-text-2": "Transferring the due date costs:",
  "ddtd-text-3": "Old due date",
  "ddtd-text-4": "New due date",
  "ddtd-text-5": "The cost will be added to the last instalment of the payment plan.",
  "ddtd-text-6": "The sum of payment may change slightly once we update the amount of interest.",
  "ddtd-text-7": "Transfer debt due date",
  "ddtd-text-8": "Transfer payment due date"
  }
  }
</i18n>
